import {
  getApiDate,
  isApiDateTimeExpired,
  parseAPIDateTime,
} from "../../../../libs/common_utils";

import { Grid, Pagination, Popup, Segment, Table } from "semantic-ui-react";

import CreativesGridContext from "../context";
import FilterControl from "./filter_control";
import { FormattedCreativeStatus } from "./formatted_creative_status";
import PropTypes from "prop-types";
import React from "react";
import { useIntl } from "react-intl";
import uuid from "uuid/v4";
import { Config } from "../../../../config/api";

/**
 * Render creative approval grid
 * @param {array} items
 * @param {object} controls
 */

export const CreativesApprovalGrid = ({ items, controls }) => {
  const intl = useIntl();
  const context = React.useContext(CreativesGridContext);

  return (
    <Segment basic loading={context.gridLoading} style={{ padding: "0" }}>
      <Grid className="common_grid">
        <Grid.Row>
          <Grid.Column>
            <FilterControl
              filter={controls.filter}
              onChange={context.filtering}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Table className="custom-table">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {intl.formatMessage({
                      defaultMessage: "Creative Name",
                      id: "LABEL_CREATIVE_NAME",
                    })}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {intl.formatMessage({
                      defaultMessage: "Advertiser",
                      id: "LABEL_ADVERTISER",
                    })}
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="left">
                    {intl.formatMessage({
                      defaultMessage: "Creative Type",
                      id: "LABEL_CREATIVE_TYPE",
                    })}
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="left">
                    {intl.formatMessage({
                      defaultMessage: "Status",
                      id: "LABEL_STATUS",
                    })}
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="left">
                    {intl.formatMessage({
                      defaultMessage: "End Date",
                      id: "LABEL_END_DATE",
                    })}
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="left" style={{ width: "30%" }}>
                    {intl.formatMessage({
                      defaultMessage: "Rejection Reason",
                      id: "LABEL_REJECTION_REASON",
                    })}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {items.map((item) => {
                  return <CreativeGridItem key={uuid()} {...item} />;
                })}
                {!items.length && (
                  <GridEmptyRow filterIsEmpty={controls.filter.isEmpty()} />
                )}
              </Table.Body>
              <Table.Footer>
                <Table.Row>
                  <Table.Cell colSpan="6" textAlign="right">
                    {controls.pager.total_pages > 1 && (
                      <Pagination
                        size="mini"
                        defaultActivePage={controls.pager.page}
                        totalPages={controls.pager.total_pages}
                        firstItem={null}
                        lastItem={null}
                        onPageChange={context.getPage}
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              </Table.Footer>
            </Table>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

CreativesApprovalGrid.propTypes = {
  items: PropTypes.array.isRequired,
  controls: PropTypes.object.isRequired,
};

const GridEmptyRow = () => {
  const intl = useIntl();

  return (
    <Table.Row>
      <Table.Cell colSpan="6" textAlign="center">
        {intl.formatMessage({
          id: "EMPTY_SEARCH_RESULTS",
          defaultMessage: "No results found",
        })}
      </Table.Cell>
    </Table.Row>
  );
};

/**
 * Generate grid item
 * @param {object} item
 * @return {JSX.Element}
 * @constructor
 */
const CreativeGridItem = (item) => {
  const intl = useIntl();

  const media_type_translation =
    item.media_type === "display"
      ? intl.formatMessage({
          id: "MEDIA_TYPE_DISPLAY",
          defaultMessage: "Display",
        })
      : intl.formatMessage({
          id: "MEDIA_TYPE_VIDEO",
          defaultMessage: "Video",
        });

  /**
   * format creative end date
   * @param {string} end_date
   * @returns {string}
   */
  const get_end_date = (end_date) => {
    const endDateObj = parseAPIDateTime(end_date);
    let creativeEndDate = getApiDate(endDateObj, Config.fullDateTimeFormat);

    if (isApiDateTimeExpired(endDateObj)) {
      const expired_translation = intl.formatMessage({
        id: "TEXT_EXPIRED",
        defaultMessage: "expired",
      });
      creativeEndDate = `${creativeEndDate} (${expired_translation})`;
    }

    return creativeEndDate;
  };

  return (
    <Table.Row className={item.status ? "" : " inactive"}>
      <Table.Cell className="text-ellipsis">{item.title}</Table.Cell>
      <Table.Cell>{item.advertiser_name}</Table.Cell>
      <Table.Cell>{media_type_translation}</Table.Cell>
      <Table.Cell>
        <FormattedCreativeStatus intl={intl} status={item.status} />
      </Table.Cell>
      <Table.Cell
        className={
          isApiDateTimeExpired(parseAPIDateTime(item.end_date))
            ? "date_expired"
            : ""
        }
      >
        {get_end_date(item.end_date)}
      </Table.Cell>
      <Table.Cell className="text-ellipsis">
        <Popup
          content={item.rejected_reason || "-"}
          disabled={!item.rejected_reason}
          size="tiny"
          inverted
          position="top left"
          trigger={<span>{item.rejected_reason || "-"}</span>}
        />
      </Table.Cell>
    </Table.Row>
  );
};
